import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Rocket, Users, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminMigrations() {
  const [boards, setBoards] = useState(null);

  async function reload() {
    try {
      const { data } = await api.get("/commissions/migrations");
      setBoards(data.boards || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load onboarding boards");
    }
  }
  useEffect(() => { reload(); }, []);

  return (
    <div className="space-y-6" data-testid="admin-migrations-page">
      <div>
        <h1 className="font-display text-3xl tracking-tight">Onboarding</h1>
        <p className="text-sm text-slate-400 mt-1">
          Onboard partner-brought clients — track each one from first contact through to live. Every partner gets their own board and a public signup URL to share.
        </p>
      </div>

      {boards === null ? (
        <div className="p-6 text-sm text-slate-500">Loading…</div>
      ) : boards.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {boards.map((b) => <BoardCard key={b.id} board={b} />)}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-10 text-center" data-testid="migrations-empty">
      <Rocket className="w-8 h-8 mx-auto text-cyan-400 mb-3" />
      <h3 className="font-display text-xl tracking-tight mb-1">No onboarding boards yet</h3>
      <p className="text-sm text-slate-400 max-w-md mx-auto">
        Open a partner from <Link to="/admin/partners" className="text-cyan-400 hover:text-cyan-300">Partners</Link>, then click <span className="text-slate-200">Open onboarding board</span> to create their first board.
      </p>
    </div>
  );
}

function BoardCard({ board }) {
  const totalStages = board.stages?.length || 0;
  return (
    <Link
      to={`/admin/onboarding/${board.partner_code}`}
      data-testid={`migration-board-${board.partner_code}`}
      className="group rounded-xl border border-slate-800 bg-slate-900 hover:border-cyan-500/50 hover:bg-slate-900/80 transition-all p-5 block"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">
            {board.partner_code}
          </div>
          <div className="font-display text-lg tracking-tight mt-1 truncate">{board.name}</div>
        </div>
        <ArrowUpRight className="w-5 h-5 text-slate-600 group-hover:text-cyan-400 transition-colors shrink-0" />
      </div>
      <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> {board.client_count} clients
        </span>
        <span>·</span>
        <span>{totalStages} stages</span>
      </div>
    </Link>
  );
}
